/** @jsx isolateComponent */

import { css } from '@emotion/core';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import React, { Fragment, useState } from 'react';
import { Animation } from '../../../theme/Animation';
import { Colors } from '../../../theme/Colors';
import { IconName } from '../../display/Icon';
import { IconButton } from '../../navigation/IconButton';
import { Indicator } from '../../navigation/Indicator';
import { OnPaginateData, Paginator } from '../../navigation/Paginator';
import { ScrollSnap } from './ScrollSnap';

type ChangeSource = 'scroll' | 'next' | 'previous' | 'paginator' | 'indicator';

type Props = {
  /** The images to render in the Carousel */
  children: any;

  /**
   * Width of each slide
   * NOTE: Widths need to match for each child element
   * */
  slideWidth: number;

  /** Margin (px) between slides */
  gap?: number;

  /**
   * Show the navigation arrows on either side of the `Carousel`.
   */
  showArrows?: boolean;

  /** The type of pagination to render below the images. */
  paginatorStyle?: 'indicator' | 'paginator';

  /** Make inactive slides slightly transparent */
  fadeInactive?: boolean;

  /**
   * Fade the edges of the `Carousel` to this color. This color is open to any
   * valid CSS string since colors outside of the design system may be used
   * as a background color.
   */
  edgeColor?: string;

  /**
   * Callback called when the active slide changes. Receives the index
   * of the newly active slide, as well as the index of the previously
   * active slide. Additional info about the change is also available,
   * which includes the triggering source of the change.
   */
  onActiveSlideChange?: (
    slideIndex: number,
    previousSlideIndex: number,
    changeInfo: {
      source: ChangeSource;
    }
  ) => void;
};

type CarouselDerivedState = {
  isFirstPage: boolean;
  isLastPage: boolean;
};

type ArrowButtonProps = {
  isDisabled: boolean;
  direction: 'prev' | 'next';
  onClick: () => void;
};

const Styles = labelStyles('Carousel', {
  root: (props: Props, { isFirstPage, isLastPage }: CarouselDerivedState) => [
    css({
      display: 'grid',
      gridTemplateColumns: '100%',
      gridTemplateRows: 'auto',
      gap: 30,
      position: 'relative',
      justifyItems: 'center',
    }),
    props.paginatorStyle &&
      withModifier('paginator', {
        gridTemplateRows: 'auto auto',
      }),
    (props.edgeColor || props.showArrows) &&
      withModifier('fadeEdges', {
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          zIndex: 1,
          top: 0,
          bottom: 0,
          width: 50,
          pointerEvents: 'none',
          transition: Animation.transitionValue,
        },
        '&::before': {
          opacity: isFirstPage ? 0 : 1,
          left: 0,
          right: 'auto',
          background: `linear-gradient(to right, ${
            props.edgeColor || Colors.white
          }, #fff0)`,
        },
        '&::after': {
          opacity: isLastPage ? 0 : 1,
          left: 'auto',
          right: 0,
          background: `linear-gradient(to left, ${
            props.edgeColor || Colors.white
          }, #fff0)`,
        },
      }),
  ],
  arrowButton: (props: ArrowButtonProps) => [
    css({
      transition: Animation.transitionValue,
      position: 'absolute',
      zIndex: 2,
      top: 'calc(50% - 40px)',
      transform: 'translateY(50%)',
    }),
    withModifier(
      props.direction,
      {
        prev: {
          left: 0,
        },
        next: {
          right: 0,
        },
      }[props.direction]
    ),
    props.isDisabled &&
      withModifier('disabled', {
        pointerEvents: 'none',
      }),
  ],
  scrollSnapChild: ({ fadeInactive }: Props, isActiveSlide: boolean) => [
    css({
      transition: 'opacity 0.5s',
      height: '100%',
    }),
    fadeInactive &&
      !isActiveSlide &&
      withModifier('faded', {
        opacity: 0.3,
      }),
  ],
});

const arrowButtonColorTheme = {
  custom: {
    base: {
      backgroundColor: Colors.gray60,
      color: Colors.white,
    },
    disabled: {
      backgroundColor: Colors.gray40,
      color: Colors.white,
    },
    hover: {
      backgroundColor: Colors.gray80,
      color: Colors.white,
    },
    focus: {
      backgroundColor: Colors.gray60,
      color: Colors.white,
      outlineColor: Colors.gray60,
    },
  },
};

const ArrowButton = (props: ArrowButtonProps) => {
  return (
    <span css={Styles.arrowButton(props)}>
      <IconButton
        ariaLabel={`${props.direction} image`}
        color="custom"
        colorTheme={arrowButtonColorTheme}
        disabled={props.isDisabled}
        icon={
          {
            prev: 'arrowSmallLeft',
            next: 'arrowSmallRight',
          }[props.direction] as IconName
        }
        isPill={true}
        onClick={props.onClick}
        size="medium"
      />
    </span>
  );
};

const defaultProps = {
  gap: 10,
  children: [],
};

/**
 * Carousel component for rendering a scrollable list of children in carousel style.
 */
export const Carousel = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };

  const [pageIndex, setPageIndex] = useState(0);
  const isFirstPage = pageIndex === 0;
  const isLastPage = pageIndex === props.children.length - 1;

  const changePageIndex = (nextIndex: number, source: ChangeSource) => {
    setPageIndex(nextIndex);
    if (props.onActiveSlideChange && pageIndex !== nextIndex) {
      props.onActiveSlideChange(nextIndex, pageIndex, { source });
    }
  };

  const paginator =
    props.paginatorStyle &&
    {
      paginator: (
        <Paginator
          currentPageIndex={pageIndex}
          onPaginate={({ pageIndex: index }: OnPaginateData) =>
            changePageIndex(index, 'paginator')
          }
          pageUrls={Array.from({ length: props.children.length })}
        />
      ),
      indicator: (
        <Indicator
          ariaLabel="List of images"
          color="grey"
          count={props.children.length}
          noun="image"
          onChangeIndex={(index) => changePageIndex(index, 'indicator')}
          selectedIndex={pageIndex}
        />
      ),
    }[props.paginatorStyle];

  return (
    <div css={Styles.root(props, { isFirstPage, isLastPage })}>
      {props.showArrows && (
        <Fragment>
          <ArrowButton
            direction="prev"
            isDisabled={isFirstPage}
            onClick={() => {
              changePageIndex(pageIndex - 1, 'previous');
            }}
          />
          <ArrowButton
            direction="next"
            isDisabled={isLastPage}
            onClick={() => {
              changePageIndex(pageIndex + 1, 'next');
            }}
          />
        </Fragment>
      )}

      <ScrollSnap
        gap={props.gap}
        itemWidth={props.slideWidth}
        onScroll={(index: number) => {
          changePageIndex(index, 'scroll');
        }}
        pageIndex={pageIndex}
        scrollSnapAlign="center"
      >
        {React.Children.map(props.children, (child, i) => (
          <div css={Styles.scrollSnapChild(props, pageIndex === i)} key={i}>
            {child}
          </div>
        ))}
      </ScrollSnap>

      {paginator}
    </div>
  );
};
